import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../api/axios';
import { Chargement, Message } from '../components/charte/Elements';
import BoutonAide from '../components/charte/BoutonAide';
import ReglesMotDePasse from '../components/ReglesMotDePasse';
import { motDePasseConforme } from '../auth/motDePasse';
import { AIDE } from '../aide/textesAide';
import { chargerMissions, Mission } from '../donnees/missions';
import { jourComplet } from '../donnees/situation';

/* Compte, d'après la maquette Compte.dc.html : « Vous » (profil), mot de
   passe, déconnexion ; à droite l'organisation chez Tribe et les contacts.
   Routes de user-service (a370081), jeton client en Bearer :
   GET et PUT /user/client-profile, POST /user/client-password. Les chiffres
   de l'organisation viennent des missions déjà lues par le portail
   (GET /quest/client/{email}). */

interface Profil {
  email: string;
  nom: string | null;
  organisation: string | null;
  telephone: string | null;
  emailVerifie?: boolean;
  creeLe?: string | null;
}

const RESEAU = 'Impossible de joindre Tribe. Vérifiez votre connexion puis réessayez.';

const Compte: React.FC = () => {
  const { user, logout, actualiserProfil } = useAuth();
  const navigate = useNavigate();

  const [profil, setProfil] = useState<Profil | null>(null);
  const [erreurProfil, setErreurProfil] = useState('');
  const [form, setForm] = useState({ nom: '', organisation: '', telephone: '' });
  const [enregistrement, setEnregistrement] = useState(false);
  const [msgProfil, setMsgProfil] = useState<{ genre: 'succes' | 'erreur'; texte: string } | null>(null);

  const [mdp, setMdp] = useState({ ancien: '', nouveau: '', confirmation: '' });
  const [envoiMdp, setEnvoiMdp] = useState(false);
  const [msgMdp, setMsgMdp] = useState<{ genre: 'succes' | 'erreur'; texte: string } | null>(null);

  const [missions, setMissions] = useState<Mission[] | null>(null);

  useEffect(() => { document.title = 'Compte — Tribe'; }, []);

  useEffect(() => {
    if (!user?.email) return;
    chargerMissions(user.email).then(setMissions).catch(() => setMissions(null));
  }, [user?.email]);

  useEffect(() => {
    userApi.get('/user/client-profile')
      .then(res => {
        const p: Profil = res.data;
        setProfil(p);
        setForm({ nom: p.nom ?? '', organisation: p.organisation ?? '', telephone: p.telephone ?? '' });
      })
      .catch(err => setErreurProfil(err?.response ? 'Votre profil n\'a pas pu être chargé. Rechargez la page dans un instant.' : RESEAU));
  }, []);

  const modifie = !!profil && (
    form.nom.trim() !== (profil.nom ?? '') ||
    form.organisation.trim() !== (profil.organisation ?? '') ||
    form.telephone.trim() !== (profil.telephone ?? ''));

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsgProfil(null);
    if (!form.nom.trim()) { setMsgProfil({ genre: 'erreur', texte: 'Le nom est obligatoire.' }); return; }
    setEnregistrement(true);
    try {
      const res = await userApi.put('/user/client-profile', {
        nom: form.nom.trim(), organisation: form.organisation.trim(), telephone: form.telephone.trim(),
      });
      const p: Profil = res.data;
      setProfil(p);
      setForm({ nom: p.nom ?? '', organisation: p.organisation ?? '', telephone: p.telephone ?? '' });
      actualiserProfil({ nom: p.nom, organisation: p.organisation });
      setMsgProfil({ genre: 'succes', texte: 'Profil enregistré.' });
    } catch (err: any) {
      setMsgProfil({ genre: 'erreur', texte: err?.response?.status === 400
        ? 'Vérifiez les champs : le nom est obligatoire et le téléphone ne doit contenir que des chiffres, espaces, +, - ou parenthèses.'
        : err?.response ? 'Le profil n\'a pas pu être enregistré. Réessayez dans quelques minutes.' : RESEAU });
    } finally {
      setEnregistrement(false);
    }
  };

  const changerMdp = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsgMdp(null);
    if (!mdp.ancien) { setMsgMdp({ genre: 'erreur', texte: 'Saisissez votre mot de passe actuel.' }); return; }
    if (!motDePasseConforme(mdp.nouveau)) { setMsgMdp({ genre: 'erreur', texte: 'Le nouveau mot de passe ne respecte pas les règles indiquées.' }); return; }
    if (mdp.nouveau !== mdp.confirmation) { setMsgMdp({ genre: 'erreur', texte: 'Les deux nouveaux mots de passe ne correspondent pas.' }); return; }
    if (mdp.nouveau === mdp.ancien) { setMsgMdp({ genre: 'erreur', texte: 'Le nouveau mot de passe doit être différent de l\'actuel.' }); return; }
    setEnvoiMdp(true);
    try {
      await userApi.post('/user/client-password', { ancienMotDePasse: mdp.ancien, nouveauMotDePasse: mdp.nouveau });
      setMdp({ ancien: '', nouveau: '', confirmation: '' });
      setMsgMdp({ genre: 'succes', texte: 'Mot de passe changé. Utilisez-le à votre prochaine connexion.' });
    } catch (err: any) {
      // Règles vérifiées ci-dessus : un 400 signifie que l'ancien mot de passe est faux.
      setMsgMdp({ genre: 'erreur', texte: err?.response?.status === 400
        ? 'Mot de passe actuel incorrect.'
        : err?.response ? 'Le mot de passe n\'a pas pu être changé. Réessayez dans quelques minutes.' : RESEAU });
    } finally {
      setEnvoiMdp(false);
    }
  };

  const deconnexion = async () => {
    await logout();
    navigate('/connexion');
  };

  const lieuxLivres = missions?.filter(m => m.statut === 'terminee').reduce((t, m) => t + (m.collectesRealisees ?? 0), 0);
  const depuis = profil?.creeLe
    ? new Date(profil.creeLe).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }).replace(/^./, c => c.toUpperCase())
    : null;

  return (
    <div className="page" style={{ gap: '28px' }}>
      <header className="pile" style={{ gap: '6px' }}>
        <div className="surtitre">Compte{user?.organisation ? ` · ${user.organisation}` : ''}</div>
        <h1>Votre compte</h1>
      </header>

      <div className="grille-compte">
        <div className="pile" style={{ gap: '20px' }}>
          <section className="carte compte__section" aria-labelledby="titre-profil">
            <div className="ligne ligne--entre" style={{ alignItems: 'baseline' }}>
              <h2 id="titre-profil" style={{ fontSize: '20px' }}>Vous</h2>
              {profil?.creeLe && <span style={{ fontSize: '12px', color: 'var(--gris)' }}>Compte créé le {jourComplet(profil.creeLe)}</span>}
            </div>
            {erreurProfil ? <Message genre="erreur">{erreurProfil}</Message> : !profil ? <Chargement /> : (
              <form onSubmit={enregistrer} className="pile" style={{ gap: '18px' }}>
                <div className="grille-2 champs-mono">
                  <div className="champ">
                    <label htmlFor="profil-nom">Prénom et nom</label>
                    <input id="profil-nom" autoComplete="name" value={form.nom} maxLength={120}
                      onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} required />
                  </div>
                  <div className="champ">
                    <label htmlFor="profil-telephone">Téléphone</label>
                    <input id="profil-telephone" type="tel" autoComplete="tel" value={form.telephone} maxLength={25}
                      placeholder="+221 77 000 00 00" onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} />
                  </div>
                  <div className="champ">
                    <div className="ligne" style={{ gap: '6px' }}>
                      <label htmlFor="profil-organisation">Organisation</label>
                      <BoutonAide texte={AIDE.organisation} />
                    </div>
                    <input id="profil-organisation" autoComplete="organization" value={form.organisation} maxLength={255}
                      onChange={e => setForm(f => ({ ...f, organisation: e.target.value }))} />
                  </div>
                  <div className="champ">
                    <span id="profil-email-libelle" className="champ__libelle">Adresse de connexion</span>
                    <div className="ligne" style={{ gap: '10px', padding: '12px 0', fontSize: '15px' }} aria-labelledby="profil-email-libelle">
                      <span style={{ overflowWrap: 'anywhere' }}>{profil.email}</span>
                      {profil.emailVerifie && (
                        <span className="ligne" style={{ gap: '6px', fontSize: '12px', fontWeight: 600, color: '#356A0F', flexWrap: 'nowrap' }}>
                          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
                          Confirmée
                        </span>
                      )}
                    </div>
                    <p className="champ__aide" style={{ fontSize: '12px' }}>
                      Pour la changer, écrivez à <a href="mailto:contact@tribe.sn">contact@tribe.sn</a>.
                    </p>
                  </div>
                </div>
                {msgProfil && <Message genre={msgProfil.genre} onFermer={() => setMsgProfil(null)}>{msgProfil.texte}</Message>}
                <div className="ligne" style={{ justifyContent: 'flex-end' }}>
                  <button type="submit" className="bouton bouton--sombre" disabled={!modifie || enregistrement}>
                    {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            )}
          </section>

          <section className="carte compte__section" aria-labelledby="titre-mdp">
            <h2 id="titre-mdp" style={{ fontSize: '20px' }}>Mot de passe</h2>
            <form onSubmit={changerMdp} className="pile" style={{ gap: '18px' }}>
              <div className="grille-3 champs-mono">
                <div className="champ">
                  <label htmlFor="mdp-ancien">Actuel</label>
                  <input id="mdp-ancien" aria-label="Mot de passe actuel" type="password" autoComplete="current-password" value={mdp.ancien} placeholder="••••••••"
                    onChange={e => setMdp(m => ({ ...m, ancien: e.target.value }))} />
                </div>
                <div className="champ">
                  <label htmlFor="mdp-nouveau">Nouveau</label>
                  <input id="mdp-nouveau" aria-label="Nouveau mot de passe" type="password" autoComplete="new-password" value={mdp.nouveau} aria-describedby="mdp-regles"
                    placeholder="8 caractères minimum" onChange={e => setMdp(m => ({ ...m, nouveau: e.target.value }))} />
                </div>
                <div className="champ">
                  <label htmlFor="mdp-confirmation">Confirmer</label>
                  <input id="mdp-confirmation" aria-label="Confirmer le nouveau mot de passe" type="password" autoComplete="new-password" value={mdp.confirmation} placeholder="••••••••"
                    onChange={e => setMdp(m => ({ ...m, confirmation: e.target.value }))} />
                </div>
              </div>
              <ReglesMotDePasse id="mdp-regles" motDePasse={mdp.nouveau} />
              {msgMdp && <Message genre={msgMdp.genre} onFermer={() => setMsgMdp(null)}>{msgMdp.texte}</Message>}
              <div className="ligne" style={{ justifyContent: 'flex-end' }}>
                <button type="submit" className="bouton bouton--secondaire" disabled={envoiMdp}>{envoiMdp ? 'Enregistrement…' : 'Changer le mot de passe'}</button>
              </div>
            </form>
          </section>

          <section className="compte__sortie">
            <p style={{ fontSize: '14px', color: 'var(--texte-2)' }}>
              Vous quittez le portail ? Vos missions et documents restent accessibles à votre organisation.
            </p>
            <button type="button" className="lien-sortie" onClick={deconnexion}>Se déconnecter</button>
          </section>
        </div>

        <aside className="pile" style={{ gap: '16px' }}>
          <section className="carte carte--forte" style={{ padding: '22px 24px', gap: '14px' }} aria-labelledby="titre-organisation">
            <span id="titre-organisation" className="etiquette-mono" style={{ color: 'var(--ocre-clair)' }}>Votre organisation chez Tribe</span>
            <dl className="compte__chiffres">
              <div><dt>Entité facturante</dt><dd style={{ fontWeight: 600 }}>Tribe Sénégal SAS</dd></div>
              <div><dt>Missions</dt><dd className="mono">{missions ? missions.length.toLocaleString('fr-FR') : '—'}</dd></div>
              <div><dt>Lieux livrés</dt><dd className="mono">{lieuxLivres != null ? lieuxLivres.toLocaleString('fr-FR') : '—'}</dd></div>
              <div><dt>Depuis</dt><dd>{depuis ?? '—'}</dd></div>
            </dl>
            <p style={{ fontSize: '12px', color: 'rgba(242,232,212,0.6)' }}>
              Raison sociale, adresse et identifiant fiscal figurent sur vos documents. Pour les modifier, écrivez à Tribe.
            </p>
          </section>
          <section className="carte" style={{ padding: '20px 22px', gap: '10px' }} aria-labelledby="titre-contacts">
            <span id="titre-contacts" className="etiquette-mono" style={{ color: 'var(--vert)' }}>Contacts Tribe</span>
            <ul className="pile" style={{ gap: '8px', fontSize: '14px', listStyle: 'none' }}>
              <li><div style={{ fontWeight: 600 }}>Missions et devis</div><a href="mailto:missions@tribe.sn">missions@tribe.sn</a></li>
              <li><div style={{ fontWeight: 600 }}>Factures et règlements</div><a href="mailto:facturation@tribe.sn">facturation@tribe.sn</a></li>
              <li><div style={{ fontWeight: 600 }}>Accès et données</div><a href="mailto:contact@tribe.sn">contact@tribe.sn</a></li>
            </ul>
          </section>
          <div className="ligne" style={{ gap: '14px', fontSize: '13px', padding: '0 4px' }}>
            <a href="/legal" style={{ color: 'var(--gris)', textDecoration: 'underline' }}>Mentions légales</a>
            <a href="/legal" style={{ color: 'var(--gris)', textDecoration: 'underline' }}>Confidentialité</a>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Compte;
